/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { Theme } from '@/enums/theme';
import { useStorage } from '../hooks/use-storage';

type ThemeType = Theme;

interface ThemeContextType {
  theme: ThemeType;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setTheme] = useState<ThemeType>(Theme.LIGHT);
  const storage = useStorage();

  const loadThemePreference = useCallback(async () => {
    try {
      const savedTheme = await storage.getItem('theme');

      if (savedTheme === Theme.DARK || savedTheme === Theme.LIGHT) {
        setTheme(savedTheme as ThemeType);
      }
    } catch (error) {
      console.error('Error loading theme preference:', error);
    }
  }, [storage]);

  useEffect(() => {
    // Load saved theme from storage on mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadThemePreference();
  }, [loadThemePreference]);

  useEffect(() => {
    if (theme === Theme.DARK) {
      document.documentElement.classList.add(Theme.DARK);
    } else {
      document.documentElement.classList.remove(Theme.DARK);
    }
  }, [theme]);

  const toggleTheme = async () => {
    const newTheme = theme === Theme.LIGHT ? Theme.DARK : Theme.LIGHT;
    setTheme(newTheme);

    try {
      await storage.setItem('theme', newTheme);
    } catch (error) {
      console.error('Error saving theme preference:', error);
    }
  };

  const contextValue: ThemeContextType = {
    theme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};

// Export for backward compatibility
export { ThemeProvider as UnifiedThemeProvider };
